import { useState, useEffect } from 'react';
import { libraryAPI } from '../../utils/api';
import PublicLayout from '../../components/layout/PublicLayout';
import { MapPinIcon, PhoneIcon, EnvelopeIcon, ClockIcon } from '@heroicons/react/24/outline';
import useSEO from '../../hooks/useSEO';

export default function LibraryPage() {
  useSEO({ title: 'Library — JNF', description: 'Visit the JNF physical library. Explore our collection of Islamic books, Tijaniyyah literature and spiritual resources.' });

  const [info, setInfo]         = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      libraryAPI.getInfo(),
      libraryAPI.getSections(),
    ]).then(([i, s]) => {
      setInfo(i.data.data || null);
      setSections(s.data.data || []);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>

      {/* ── Hero banner ───────────────────────────────────────── */}
      {info?.cover_url ? (
        <div className="relative w-full h-64 md:h-80 overflow-hidden">
          <img src={info.cover_url} alt="JNF Library" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 to-transparent flex items-end">
            <div className="max-w-7xl mx-auto px-4 pb-8 w-full">
              <p className="text-cyan-400 text-xs font-semibold uppercase tracking-widest mb-1">✦ Knowledge is Light ✦</p>
              <h1 className="text-white text-3xl md:text-4xl font-bold">{info?.name || 'JNF Library'}</h1>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-navy-700 to-navy-900 py-16 px-4 text-center">
          <p className="text-cyan-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Knowledge is Light ✦</p>
          <h1 className="text-white text-3xl md:text-4xl font-bold mb-2">{info?.name || 'JNF Library'}</h1>
          <p className="arabic text-cyan-300 text-lg mt-3">اقرأ باسم ربك الذي خلق</p>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-12">

        {loading ? (
          <div className="space-y-6">
            <div className="h-40 bg-gray-100 rounded-2xl animate-pulse" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3].map(i => <div key={i} className="h-72 bg-gray-100 rounded-2xl animate-pulse" />)}
            </div>
          </div>
        ) : (
          <>
            {/* ── General info block ────────────────────────── */}
            {info && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 mb-12">
                <div className="grid md:grid-cols-2 gap-8 items-start">

                  {/* Description */}
                  <div>
                    <h2 className="text-xl font-bold text-navy-800 mb-3">About the Library</h2>
                    {info.description && (
                      <p className="text-gray-600 leading-relaxed text-sm md:text-base whitespace-pre-line">
                        {info.description}
                      </p>
                    )}
                  </div>

                  {/* Contact + hours */}
                  <div className="space-y-3">
                    {info.opening_hours && (
                      <div className="flex items-start gap-3 p-4 bg-cyan-50 rounded-xl border border-cyan-100">
                        <ClockIcon className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-cyan-700 uppercase tracking-wide mb-1">Opening Hours</p>
                          <p className="text-navy-800 text-sm whitespace-pre-line">{info.opening_hours}</p>
                        </div>
                      </div>
                    )}
                    {info.address && (
                      <div className="flex items-start gap-3">
                        <MapPinIcon className="h-5 w-5 text-navy-400 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-600 text-sm">{info.address}</p>
                      </div>
                    )}
                    {info.phone && (
                      <div className="flex items-center gap-3">
                        <PhoneIcon className="h-5 w-5 text-navy-400 flex-shrink-0" />
                        <a href={`tel:${info.phone}`} className="text-gray-600 text-sm hover:text-cyan-600 transition-colors">
                          {info.phone}
                        </a>
                      </div>
                    )}
                    {info.email && (
                      <div className="flex items-center gap-3">
                        <EnvelopeIcon className="h-5 w-5 text-navy-400 flex-shrink-0" />
                        <a href={`mailto:${info.email}`} className="text-gray-600 text-sm hover:text-cyan-600 transition-colors">
                          {info.email}
                        </a>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )}

            {/* ── Sections ──────────────────────────────────── */}
            {sections.length > 0 && (
              <>
                <div className="mb-8">
                  <h2 className="section-title mb-2">Library Sections</h2>
                  <p className="text-gray-400 text-sm">Explore our collection — arranged by subject and tradition</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sections.map(section => (
                    <div key={section.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">

                      {/* Photo */}
                      <div className="h-52 relative overflow-hidden bg-navy-100">
                        {section.cover_url ? (
                          <img
                            src={section.cover_url}
                            alt={section.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-600 to-navy-900">
                            <span className="text-6xl">📚</span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <h3 className="font-bold text-navy-800 text-lg mb-1 group-hover:text-cyan-600 transition-colors">
                          {section.name}
                        </h3>
                        {section.name_ar && (
                          <p className="arabic text-navy-500 text-base mb-2">{section.name_ar}</p>
                        )}
                        {section.description && (
                          <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                            {section.description}
                          </p>
                        )}

                        {/* Hours + Location */}
                        <div className="space-y-1.5 pt-3 border-t border-gray-100">
                          {section.opening_hours && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <ClockIcon className="h-3.5 w-3.5 text-cyan-400 flex-shrink-0" />
                              {section.opening_hours}
                            </div>
                          )}
                          {section.location_note && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <MapPinIcon className="h-3.5 w-3.5 text-cyan-400 flex-shrink-0" />
                              {section.location_note}
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Empty state */}
            {sections.length === 0 && !loading && (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">📚</div>
                <p className="text-gray-400 font-medium">Library sections coming soon, in sha Allah.</p>
              </div>
            )}
          </>
        )}
      </div>

    </PublicLayout>
  );
}
